import { enqueueSnackbar } from 'notistack';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';

import { MLInput } from 'components/Input/Input';
import { useCreateTechnicianForClient } from 'hooks/Clients/smartUsersForClientCreate/useCreateTechnicianForClient';

interface CreateTechnicianModalProps {
  isOpen: boolean;
  onClose: () => void;
  isClientModal: boolean; // Optional prop to determine if it's opened from client or site modal
}

const CreateTechnicianModal = ({ isOpen, onClose, isClientModal }: CreateTechnicianModalProps) => {
  const { createTechnician, isLoading: isLoadingTechnician } = useCreateTechnicianForClient();

  const { t } = useTranslation();

  const {
    handleSubmit: handleTechnicianSubmit,
    control: technicianControl,
    reset: resetTechnician,
    setError: setTechnicianError,
  } = useForm({
    defaultValues: {
      first_name: '',
      last_name: '',
      email: '',
      phone_number: '',
      password: '',
      confirm_password: '',
      send_email_notifications: false,
    },
  });

  const onCloseModal = () => {
    onClose();
    resetTechnician();
  };

  const onCreateTechnician = (technicianData: any) => {
    createTechnician(technicianData, {
      onSuccess: () => {
        enqueueSnackbar(t('Modals.manage-technician.successCreate'), { variant: 'success' });
        onCloseModal();
      },
      onError: (error: any) => {
        handleTechnicianError(error);
        enqueueSnackbar(t('Modals.manage-technician.failCreate'), { variant: 'error' });
      },
    });
  };

  const handleTechnicianError = (error: any) => {
    if (error.response?.data) {
      const errorData = error.response.data;

      // Handle field-specific errors
      if (errorData.first_name) {
        setTechnicianError('first_name', { message: errorData.first_name[0] });
      }
      if (errorData.last_name) {
        setTechnicianError('last_name', { message: errorData.last_name[0] });
      }
      if (errorData.email) {
        setTechnicianError('email', { message: errorData.email[0] });
      }
      if (errorData.phone_number) {
        setTechnicianError('phone_number', { message: errorData.phone_number[0] });
      }
      if (errorData.password) {
        setTechnicianError('password', { message: errorData.password[0] });
      }
      if (errorData.confirm_password) {
        setTechnicianError('confirm_password', { message: errorData.confirm_password[0] });
      }

      // Handle non-field errors (like password mismatch)
      if (errorData.non_field_errors) {
        setTechnicianError('confirm_password', { message: errorData.non_field_errors[0] });
        setTechnicianError('password', { message: errorData.non_field_errors[0] });
      }
    } else {
      enqueueSnackbar(t('Modals.manage-technician.failCreate'), { variant: 'error' });
    }
  };

  return (
    <div>
      <Modal
        isOpen={isOpen}
        toggle={() => {
          // onReset();
          onClose();
        }}
        size="lg"
        backdrop="static"
        keyboard={false}
      >
        <ModalHeader
          className="border-bottom py-3"
          toggle={() => {
            // onReset();
            onClose();
          }}
        >
          <div className="d-flex justify-content-between align-items-center w-100 gap-5">
            <div className="text-purple">
              {isClientModal ? t('Modals.manage-technician.header-client') : t('Modals.manage-technician.header-site')}
            </div>
            <Button className="btn btn-ghost-purple btn-sm" onClick={() => onCloseModal()}>
              {isClientModal
                ? t('Modals.manage-client.backToClientCreation')
                : t('Modals.manage-client.backToSiteCreation')}
            </Button>
          </div>
        </ModalHeader>
        <ModalBody>
          <div className="row">
            <div className="col-12">
              <div className="row mb-3">
                <h5 className="mb-1">{t('Modals.manage-client.createNewTechnician')}</h5>
              </div>
              {/* <h5 className="mb-3">{t('Modals.manage-technician.title')}</h5> */}
              <div className="row mb-3">
                <div className="col-md-6">
                  <Controller
                    control={technicianControl}
                    name="first_name"
                    render={({ field, fieldState: { error } }) => (
                      <MLInput
                        {...field}
                        label={t('Modals.manage-technician.firstName')}
                        error={error?.message}
                        required
                      />
                    )}
                  />
                </div>
                <div className="col-md-6">
                  <Controller
                    control={technicianControl}
                    name="last_name"
                    render={({ field, fieldState: { error } }) => (
                      <MLInput
                        {...field}
                        label={t('Modals.manage-technician.lastName')}
                        error={error?.message}
                        required
                      />
                    )}
                  />
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-6">
                  <Controller
                    control={technicianControl}
                    name="email"
                    render={({ field, fieldState: { error } }) => (
                      <MLInput
                        {...field}
                        label={t('Modals.manage-technician.email')}
                        type="email"
                        error={error?.message}
                        required
                      />
                    )}
                  />
                  <Controller
                    control={technicianControl}
                    name="send_email_notifications"
                    render={({ field }) => (
                      <>
                        <div className="form-check mt-1">
                          <Input
                            className="form-check-input"
                            type="checkbox"
                            id="smstReceiveMessagesCheckbox"
                            checked={field.value}
                            onChange={e => field.onChange(e.target.checked)}
                          />
                          <Label className="form-check-label mb-0 text-muted" htmlFor="smstReceiveMessagesCheckbox">
                            Send account credentials via email
                          </Label>
                        </div>
                      </>
                    )}
                  />
                </div>
                <div className="col-md-6">
                  <Controller
                    control={technicianControl}
                    name="phone_number"
                    render={({ field, fieldState: { error } }) => (
                      <MLInput
                        {...field}
                        label={t('Modals.manage-technician.phone')}
                        type="tel"
                        error={error?.message}
                      />
                    )}
                  />
                </div>
              </div>

              <div className="row">
                <div className="col-md-6">
                  <Controller
                    control={technicianControl}
                    name="password"
                    render={({ field, fieldState: { error } }) => (
                      <MLInput
                        {...field}
                        label={t('Modals.manage-technician.password')}
                        type="password"
                        error={error?.message}
                        required
                      />
                    )}
                  />
                </div>
                <div className="col-md-6">
                  <Controller
                    control={technicianControl}
                    name="confirm_password"
                    render={({ field, fieldState: { error } }) => (
                      <MLInput
                        {...field}
                        label={t('Modals.manage-technician.confirmPassword')}
                        type="password"
                        error={error?.message}
                        required
                      />
                    )}
                  />
                </div>
              </div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button className="btn btn-ghost-danger" onClick={onCloseModal}>
            {t('Modals.delete-modal.cancel')}
          </Button>
          <Button
            className="btn btn-ghost-success"
            onClick={handleTechnicianSubmit(onCreateTechnician)}
            disabled={isLoadingTechnician}
          >
            {isLoadingTechnician ? t('Modals.manage-client.creating') : t('Modals.manage-technician.createTechnician')}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default CreateTechnicianModal;
